/**
 * Tenant Detail Page - Phase D Enhanced
 * Modern SaaS Control Plane tenant detail with tabbed layout
 * Tabs: Overview, Subscription, Modules, Limits, Users, Provisioning, Audit, Support
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAdminAuth } from '../auth/AdminAuthContext';
import { PermissionGate, usePermission } from '../components';
import {
  VIEW as AUDIT_VIEW, resolveView as resolveAuditView, tenantScopedQuery,
  summaryOf, detailRows, impersonationOf, hasChange,
} from '../audit/auditPresentation';

import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import StatusBadge from '../../../components/admin/StatusBadge';
import { describeEnvironment } from './environmentPresentation';
import DemoConnectionsCard from './DemoConnectionsCard';
import PasswordSetupCell from './PasswordSetupCell';
import RepairProvisioningAction from './RepairProvisioningAction';
import { Progress } from '../../../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../components/ui/tabs';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import { Input } from '../../../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '../../../components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../../../components/ui/alert-dialog';
import {
  ArrowLeft,
  Building2,
  Users,
  Calendar,
  Clock,
  CreditCard,
  Shield,
  Loader2,
  AlertCircle,
  CheckCircle,
  XCircle,
  Pause,
  Play,
  Trash2,
  Boxes,
  Activity,
  BarChart3,
  HardDrive,
  RefreshCw,
  Mail,
  Key,
  EyeOff,
  Server,
  RotateCcw,
  Send,
  Wrench,
  History,
  ChevronDown,
  Rocket,
  UserPlus,
  UserCog,
  LogIn,
  Pencil
} from 'lucide-react';
import axios from 'axios';
import toast from 'react-hot-toast';
import TenantLicensesTab from './TenantLicensesTab';
import ProductEditionSection from './ProductEditionSection';
import ProductAddonsSection from './ProductAddonsSection';
import DocFlowStorageExtraCard from './DocFlowStorageExtraCard';
import { includedWithName, showsAsOwnTile } from '../modules/moduleHeld';
import TenantVersionTab from './TenantVersionTab';
import TenantEmailsTab from './TenantEmailsTab';
import OnboardingPanel from './OnboardingPanel';
import ImpersonationReasonDialog from './ImpersonationReasonDialog';
import { describeStage } from './provisioningStages';
import { evidenceKeyLabel, evidenceRows } from './readinessEvidence';
import { describeProgress, needsAttention, describeDrift } from './provisioningProgress';
import { canSuspend, canReactivate, canSetReadOnly, canTerminate, terminalNotice } from './tenantLifecycle';
import TerminateTenantControl, { refusalText } from './TerminateTenantControl';
import SubscriptionBillingTab from './SubscriptionBillingTab';
import TenantProductCard from './TenantProductCard';
import ProductSetupOutcome from './ProductSetupOutcome';
import { productLabel } from './productLabels';
import { TenantProductsLimits } from '../quotas';
import {
  DEFAULT_PASSWORD_POLICY,
  normalisePasswordPolicy,
  passwordPolicyErrors,
} from './passwordPolicy';
import { moduleToggleOutcome } from './moduleToggleOutcome';

//: One page of tenant audit history. Bounded on purpose — the endpoint caps at
//: 100 and an investigation pages rather than scrolls.
const AUDIT_PAGE_SIZE = 25;

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const ADMIN_API = `${BACKEND_URL}/api/admin`;
const CONTROL_PLANE_API = `${BACKEND_URL}/api/admin/control-plane`;

// "Not in the hierarchy" is a real, selectable state — a correctly provisioned
// System Administrator carries exactly this. It needs a sentinel because a
// Select item cannot hold an empty value.
const NO_HIERARCHY_ROLE = '__none__';

const TenantDetailPage = () => {
  const { tenantId } = useParams();
  const { getAdminToken, can, adminUser } = useAdminAuth();
  const navigate = useNavigate();
  
  // State
  const [tenant, setTenant] = useState(null);
  const [storageQuota, setStorageQuota] = useState(null);
  const [users, setUsers] = useState([]);
  const [modules, setModules] = useState([]);
  const [availableModules, setAvailableModules] = useState([]);
  // The Create Tenant catalogue rows (name, description, default price,
  // billing unit, what a product includes) and the runtime's answer per
  // product — so a Modules card says what Create says, plus whether the
  // product is actually live in the workspace.
  const [productRows, setProductRows] = useState({});
  const [productNames, setProductNames] = useState({});
  const [productState, setProductState] = useState(null);
  const [productStateError, setProductStateError] = useState(null);
  const [reapplying, setReapplying] = useState(false);
  const [reapplyResult, setReapplyResult] = useState(null);
  const [priceRechecking, setPriceRechecking] = useState(false);
  const [provisioningJobs, setProvisioningJobs] = useState([]);
  const [readiness, setReadiness] = useState(null);
  const [readinessLoading, setReadinessLoading] = useState(false);
  // Repair Setup. Deliberately NOT folded into Refresh: that control reloads
  // server state and must keep meaning exactly that. A button that sometimes
  // reads and sometimes enqueues a provisioning job is one an operator cannot
  // reason about before pressing it.
  const [repairPreview, setRepairPreview] = useState(null);
  const [repairing, setRepairing] = useState(false);
  const [repairResult, setRepairResult] = useState(null);
  const [auditLogs, setAuditLogs] = useState([]);
  // A SWALLOWED FETCH ERROR BECAME "no audit logs found" — on an audit surface,
  // where absence of events is itself evidence. Loading, error and empty are
  // now three different states; see `resolveView`.
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditError, setAuditError] = useState(null);
  const [auditTotal, setAuditTotal] = useState(0);
  const [auditSkip, setAuditSkip] = useState(0);
  const [auditFilters, setAuditFilters] = useState({ action: '', result: '', search: '' });
  const [expandedAudit, setExpandedAudit] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [supportReason, setSupportReason] = useState('');

  // User management (Users tab) action state
  const [busyUserId, setBusyUserId] = useState(null);
  const [resetPasswordUser, setResetPasswordUser] = useState(null);
  // Who the operator is about to sign in as; non-null opens the reason dialog.
  const [viewAsTarget, setViewAsTarget] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [resettingPassword, setResettingPassword] = useState(false);
  const [roleDialogUser, setRoleDialogUser] = useState(null);
  // The tenant's OWN persisted hierarchy roles. There is no catalogue to
  // hard-code: every tenant seeds and then edits its own.
  const [hierarchyRoles, setHierarchyRoles] = useState([]);
  const [rolesLoadError, setRolesLoadError] = useState(null);
  const [roleDialogValue, setRoleDialogValue] = useState('user');
  const [savingRole, setSavingRole] = useState(false);
  const [createUserOpen, setCreateUserOpen] = useState(false);
  const [createUserForm, setCreateUserForm] = useState({ email: '', first_name: '', last_name: '', password: '', role: 'user', role_id: null });
  const [creatingUser, setCreatingUser] = useState(false);
  const [passwordPolicy, setPasswordPolicy] = useState(DEFAULT_PASSWORD_POLICY);

  // Edit Tenant
  const [editTenantOpen, setEditTenantOpen] = useState(false);
  const [editForm, setEditForm] = useState(null);
  const [editPlans, setEditPlans] = useState([]);
  const [savingTenant, setSavingTenant] = useState(false);

  const headers = { Authorization: `Bearer ${getAdminToken()}` };
  // Resolved HERE and passed down: the Demo Connections switch and the card
  // that hosts it stay free of the auth gate (which pulls the tooltip
  // primitive this repo's Jest cannot resolve), so they remain mountable.
  const demoSwitchPermission = usePermission('tenant:update');

  const fetchStorageQuota = async () => {
    try {
      const { data } = await axios.get(`${ADMIN_API}/tenants/${tenantId}/limits/effective`, { headers });
      setStorageQuota((data.items || []).find((row) => row.key === 'storage.bytes_total') || null);
    } catch (_error) {
      setStorageQuota(null);
    }
  };

  // Fetch all tenant data
  const fetchTenantData = async () => {
    setLoading(true);
    try {
      const [tenantRes, usersRes, passwordPolicyRes] = await Promise.all([
        axios.get(`${ADMIN_API}/tenants/${tenantId}`, { headers }),
        axios.get(`${ADMIN_API}/tenants/${tenantId}/users`, { headers }).catch(() => ({ data: { users: [] } })),
        axios.get(`${ADMIN_API}/tenants/${tenantId}/password-policy`, { headers })
          .catch(() => ({ data: DEFAULT_PASSWORD_POLICY })),
      ]);
      
      setTenant(tenantRes.data);
      fetchStorageQuota();
      setUsers(usersRes.data.users || []);
      setPasswordPolicy(normalisePasswordPolicy(passwordPolicyRes.data));
      setError(null);
    } catch (err) {
      console.error('Failed to fetch tenant:', err);
      setError('Failed to load tenant details');
    } finally {
      setLoading(false);
    }
  };

  // Fetch modules
  const fetchModules = async () => {
    try {
      // Fetch both tenant-specific modules and available modules
      const [tenantModulesRes, availableRes] = await Promise.all([
        axios.get(`${CONTROL_PLANE_API}/tenants/${tenantId}/modules`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${CONTROL_PLANE_API}/modules/available`, { headers })
      ]);
      
      // tenant modules API returns a list directly, not {modules: [...]}
      const tenantModulesData = Array.isArray(tenantModulesRes.data) 
        ? tenantModulesRes.data 
        : (tenantModulesRes.data.modules || []);
      
      const availModules = availableRes.data.modules || [];

      setModules(tenantModulesData);
      setAvailableModules(availModules);
    } catch (err) {
      console.error('Failed to fetch modules:', err);
    }
    await Promise.all([fetchProductRows(), fetchProductState()]);
  };

  // The catalogue rows Create Tenant renders, priced by the default book.
  // A failure leaves the cards without prices rather than blanking them.
  const fetchProductRows = async () => {
    try {
      const { data } = await axios.get(`${ADMIN_API}/commercial/presentation`, { headers });
      const byCode = {};
      (data.sections || []).forEach((sec) => (sec.entitlements || []).forEach((r) => {
        byCode[r.item_code] = r;
      }));
      setProductRows(byCode);
      setProductNames(data.labels || {});
    } catch (err) {
      console.error('Failed to fetch product catalogue:', err);
    }
  };

  // What the RUNTIME allows, per product (tenant_app_assignments + Cluvik
  // AI's switch). A failed read is said, not shown as "all live".
  const fetchProductState = async () => {
    try {
      const { data } = await axios.get(`${ADMIN_API}/tenants/${tenantId}/product-state`, { headers });
      setProductState(data);
      setProductStateError(null);
    } catch (err) {
      setProductState(null);
      setProductStateError('Whether these products are live in the workspace could not be read.');
    }
  };

  // "Check again" beside an unpriced product: re-read the default book after
  // the operator sets the price in Base Prices (another tab).
  const recheckProductPrices = async () => {
    setPriceRechecking(true);
    try { await fetchProductRows(); } finally { setPriceRechecking(false); }
  };

  // Re-run the product writes that did not land, then show the read-back.
  const handleReapplyProducts = async () => {
    setReapplying(true);
    try {
      const { data } = await axios.post(
        `${ADMIN_API}/tenants/${tenantId}/product-setup/retry`, {}, { headers });
      setReapplyResult(data);
      if (data.ok) toast.success('Products switched on in the workspace');
      else toast.error('Some products are still not live in the workspace');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'The products could not be switched on');
    } finally {
      setReapplying(false);
      await fetchModules();
    }
  };

  const fetchRepairPreview = async () => {
    try {
      const res = await axios.get(
        `${ADMIN_API}/tenants/${tenantId}/provisioning/repair-preview`, { headers });
      setRepairPreview(res.data);
    } catch (err) {
      console.error('Failed to load repair preview:', err);
      setRepairPreview(null);
    }
  };

  const runRepairSetup = async () => {
    setRepairing(true);
    setRepairResult(null);
    try {
      const res = await axios.post(
        `${ADMIN_API}/tenants/${tenantId}/provisioning/repair`, {}, { headers });
      setRepairResult({ ok: true, data: res.data });
      // Re-read every surface this touches rather than assuming the outcome.
      await Promise.all([fetchReadiness(), fetchRepairPreview(), fetchProvisioningJobs()]);
      await fetchTenantData();   // the status badge moves on a successful repair
    } catch (err) {
      setRepairResult({
        ok: false,
        // A REQUEST FAILURE IS NOT A REPAIR FAILURE, and conflating them cost
        // an operator five COMPLETED repairs that every time reported "could
        // not be started". With no response at all the request died in
        // transit and the job may well be running, so the panel says only
        // what it knows and points at the table that does know.
        message: err?.response?.data?.detail?.message
          || err?.response?.data?.detail
          || (err?.response
            ? `Repair was refused (HTTP ${err.response.status}).`
            : 'No response from the server, so the repair could not be '
              + 'confirmed either way. Check Provisioning Jobs below before '
              + 'retrying — it may already be running.'),
      });
      await fetchRepairPreview();
    } finally {
      setRepairing(false);
    }
  };

  // Fetch provisioning jobs
  const fetchProvisioningJobs = async () => {
    try {
      const res = await axios.get(`${CONTROL_PLANE_API}/tenants/${tenantId}/provisioning/jobs`, { headers });
      setProvisioningJobs(res.data.jobs || []);
    } catch (err) {
      console.error('Failed to fetch provisioning jobs:', err);
    }
  };

  // Runtime readiness — derived from the PROVISION_RUNTIME job + per-step
  // report, never from Tenant.status alone (D2).
  const fetchReadiness = async () => {
    setReadinessLoading(true);
    try {
      const res = await axios.get(`${ADMIN_API}/tenants/${tenantId}/provisioning/readiness`, { headers });
      setReadiness(res.data);
    } catch (err) {
      console.error('Failed to fetch provisioning readiness:', err);
      setReadiness(null);
    } finally {
      setReadinessLoading(false);
    }
  };

  // Fetch audit logs
  const fetchAuditLogs = async (skip = auditSkip, filters = auditFilters) => {
    setAuditLoading(true);
    setAuditError(null);
    try {
      // FILTERING AND PAGING ARE THE SERVER'S JOB. Pulling the history down and
      // filtering it in React would be bounded by whatever page happened to
      // load, so a filter would silently mean "matches, among the first 50".
      // `tenantScopedQuery` refuses to build a query with no tenant scope.
      const res = await axios.get(`${ADMIN_API}/audit-logs`, {
        headers,
        params: tenantScopedQuery({ tenantId, filters, skip, limit: AUDIT_PAGE_SIZE }),
      });
      setAuditLogs(res.data.logs || []);
      setAuditTotal(res.data.total || 0);
      setAuditSkip(skip);
    } catch (err) {
      console.error('Failed to fetch audit logs:', err);
      // STATED, not swallowed. The panel says it could not read the history
      // rather than reporting the tenant as having none.
      setAuditError('Cluvik could not load this tenant\u2019s audit history.');
      setAuditLogs([]);
    } finally {
      setAuditLoading(false);
    }
  };

  useEffect(() => {
    fetchTenantData();
    // THE HEADER IS ONE OF THE PROGRESS SURFACES, so it needs the projection on
    // arrival. `fetchReadiness` used to run only for the Provisioning tab (and
    // after a repair), so `readiness` was null on Overview and the header
    // progress line — which renders from it — simply never appeared: a tenant
    // showed "10 of 20 steps · 50%" in the Overview panel with nothing beside
    // the PROVISIONING badge above it. Measured live on b8b1a444.
    //
    // One extra read per detail page view, on a single tenant. The rule the
    // list obeys is "no live measurement PER ROW"; the detail page is exactly
    // where measuring one tenant is correct.
    fetchReadiness();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only re-run when the tenant changes
  }, [tenantId]);

  useEffect(() => {
    if (activeTab === 'modules') {
      fetchModules();
      fetchTenantData(); // Refresh tenant data to get latest module_entitlements
    }
    // Products & Limits owns its authoritative read. Do not also fetch the
    // retired tenant_limits and /quotas projections when this tab opens.
    if (activeTab === 'provisioning') { fetchProvisioningJobs(); fetchReadiness(); fetchRepairPreview(); }
    if (activeTab === 'audit') fetchAuditLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only re-run when the active tab changes
  }, [activeTab]);

  // Support Actions
  const handleSupportAction = async (action) => {
    setActionLoading(true);
    try {
      const { data } = await axios.post(`${CONTROL_PLANE_API}/tenants/${tenantId}/support/action`,
        { action, reason: supportReason || `Admin action: ${action}` },
        { headers }
      );
      // HTTP 200 doesn't mean the action itself succeeded — the endpoint
      // reports per-action outcome in the body (e.g. CREATE_PAYMENT_LINK
      // deliberately returns success:false when Stripe isn't configured).
      if (data.success) {
        toast.success(data.message || 'Action completed');
      } else {
        toast.error(data.message || 'Action failed');
      }
      setSupportReason('');
      fetchTenantData();
    } catch (err) {
      console.error(`Failed to ${action}:`, err);
      toast.error(err.response?.data?.detail || `Failed to run ${action}`);
    } finally {
      setActionLoading(false);
    }
  };

  // Terminate uses the generic tenant-update endpoint (PATCH /tenants/{id}
  // with status=TERMINATED) rather than the support/action endpoint —
  // TenantSupportAction has no TERMINATE value, and TenantStatus already
  // models TERMINATED as a first-class lifecycle state.
  //
  // D3 hotfix, Option B: it carries the tenant's name as typed and a reason,
  // from TerminateTenantControl's confirmation dialog. A refusal is RE-THROWN
  // so the dialog shows it inline beside the field it names.
  const handleTerminateTenant = async ({ confirmTenantName, reason }) => {
    setActionLoading(true);
    try {
      const res = await axios.patch(`${ADMIN_API}/tenants/${tenantId}`, {
        status: 'TERMINATED', confirm_tenant_name: confirmTenantName, reason,
      }, { headers });
      if (res.status === 202) {
        // Terminated, with one repair step owed (the enforcement epoch). Said,
        // not hidden behind a plain success.
        toast(refusalText({ response: res }, 'Tenant terminated; a repair step is pending.'),
          { duration: 15000 });
      } else {
        toast.success('Tenant terminated');
      }
      fetchTenantData();
    } catch (err) {
      console.error('Failed to terminate tenant:', refusalText(err, 'Failed to terminate tenant'));
      throw err;
    } finally {
      setActionLoading(false);
    }
  };

  // User actions — Users tab
  const handleSuspendUser = async (userId) => {
    setBusyUserId(userId);
    try {
      await axios.post(`${ADMIN_API}/users/${userId}/suspend`, {}, { headers });
      toast.success('User suspended');
      fetchTenantData();
    } catch (err) {
      console.error('Failed to suspend user:', err);
      toast.error(err.response?.data?.detail || 'Failed to suspend user');
    } finally {
      setBusyUserId(null);
    }
  };

  const handleActivateUser = async (userId) => {
    setBusyUserId(userId);
    try {
      await axios.post(`${ADMIN_API}/users/${userId}/activate`, {}, { headers });
      toast.success('User activated');
      fetchTenantData();
    } catch (err) {
      console.error('Failed to activate user:', err);
      toast.error(err.response?.data?.detail || 'Failed to activate user');
    } finally {
      setBusyUserId(null);
    }
  };

  const handleDeleteUser = async (userId) => {
    setBusyUserId(userId);
    try {
      await axios.delete(`${ADMIN_API}/users/${userId}`, { headers });
      toast.success('User deleted');
      fetchTenantData();
    } catch (err) {
      console.error('Failed to delete user:', err);
      toast.error(err.response?.data?.detail || 'Failed to delete user');
    } finally {
      setBusyUserId(null);
    }
  };

  // Login as this exact user. The reason is the operator's own, asked for in
  // the dialog; the grant is minted and the tab redirected by startViewAs,
  // the one authority for the URL form. This page never builds a CRM URL.
  const handleImpersonateUser = (user) => {
    setViewAsTarget({
      tenantId,
      tenantName: tenant?.tenant_name || tenant?.company_name,
      userId: user.id,
      userEmail: user.email,
      userName: [user.first_name, user.last_name].filter(Boolean).join(' ') || undefined,
    });
  };

  const openResetPassword = (user) => {
    setResetPasswordUser(user);
    setNewPassword('');
  };

  const handleResetPassword = async () => {
    if (!resetPasswordUser || passwordPolicyErrors(newPassword, passwordPolicy).length > 0) return;
    setResettingPassword(true);
    try {
      await axios.post(
        `${ADMIN_API}/users/${resetPasswordUser.id}/reset-password`,
        { new_password: newPassword },
        { headers }
      );
      toast.success(`Password reset for ${resetPasswordUser.email}`);
      setResetPasswordUser(null);
      setNewPassword('');
    } catch (err) {
      console.error('Failed to reset password:', err);
      toast.error(err.response?.data?.detail || 'Failed to reset password');
    } finally {
      setResettingPassword(false);
    }
  };

  const openAssignRole = (user) => {
    setRoleDialogUser(user);
    // NONE is a real selection, not an empty one: it means "not in the
    // hierarchy", which is exactly what a System Administrator carries.
    setRoleDialogValue(user.role_id || NO_HIERARCHY_ROLE);
    loadHierarchyRoles();
  };

  const loadHierarchyRoles = useCallback(() => {
    setRolesLoadError(null);
    return axios.get(`${ADMIN_API}/tenants/${tenantId}/roles`, { headers })
      .then(({ data }) => setHierarchyRoles(data.roles || []))
      .catch(() => {
        // An empty picker and a failed load look identical from the outside,
        // so they must not render the same.
        setHierarchyRoles([]);
        setRolesLoadError("This tenant's roles could not be loaded.");
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId]);

  useEffect(() => {
    if (tenantId) loadHierarchyRoles();
  }, [tenantId, loadHierarchyRoles]);

  const handleAssignRole = async () => {
    if (!roleDialogUser) return;
    setSavingRole(true);
    try {
      await axios.put(
        `${ADMIN_API}/users/${roleDialogUser.id}/hierarchy-role`,
        { role_id: roleDialogValue === NO_HIERARCHY_ROLE ? null : roleDialogValue },
        { headers }
      );
      toast.success(
        roleDialogValue === NO_HIERARCHY_ROLE
          ? 'Removed from the role hierarchy. Permission sets are unchanged.'
          : 'Hierarchy role updated. Permission sets are unchanged.'
      );
      setRoleDialogUser(null);
      fetchTenantData();
    } catch (err) {
      console.error('Failed to update role:', err);
      toast.error(err.response?.data?.detail || 'Failed to update role');
    } finally {
      setSavingRole(false);
    }
  };

  const handleCreateUser = async () => {
    if (passwordPolicyErrors(createUserForm.password, passwordPolicy).length > 0) return;
    setCreatingUser(true);
    try {
      await axios.post(`${ADMIN_API}/tenants/${tenantId}/users`, createUserForm, { headers });
      toast.success('User created');
      setCreateUserOpen(false);
      setCreateUserForm({ email: '', first_name: '', last_name: '', password: '', role: 'user', role_id: null });
      fetchTenantData();
    } catch (err) {
      console.error('Failed to create user:', err);
      toast.error(err.response?.data?.detail || 'Failed to create user');
    } finally {
      setCreatingUser(false);
    }
  };

  // Edit Tenant — reuses the existing generic PATCH /tenants/{id} endpoint,
  // which already supports plan changes (cascading module_entitlements)
  // alongside the profile fields. This is also how "change a tenant's
  // plan" is done, since no dedicated plan-assignment UI exists.
  const openEditTenant = async () => {
    setEditForm({
      tenant_name: tenant.tenant_name || tenant.company_name || '',
      organization_name: tenant.organization_name || '',
      industry: tenant.industry || '',
      region: tenant.region || '',
      // NOT `|| 'free'` and NOT `|| 10`. This is a form INITIALISER whose value
      // is PATCHed back by handleSaveTenant, so a display default here becomes
      // a persisted commercial assignment: an operator editing only the name of
      // a tenant with no plan silently assigned it one called 'free' — which
      // does not exist — and plan changes cascade module_entitlements. The seat
      // default could likewise drop a tenant to 10 seats on an unrelated save.
      //
      // `x || default` in an initialiser is a different risk from the same
      // expression in a render, and the two look identical in a grep.
      seat_limit: tenant.seat_limit ?? '',
      plan: tenant.plan || '',
      // Trial. The toggle reflects the tenant; the DURATION starts empty and
      // stays empty until an operator types one.
      //
      // Not seeded with 14, and not back-computed from `trial_ends_at`, for the
      // reason the block above already gives about initialisers: a duration
      // shown here is PATCHed back, so a default would silently reset the trial
      // of every tenant whose name someone edited. Empty means "say nothing
      // about the duration", and handleSaveTenant sends the trial fields only
      // when one of the two actually changed.
      is_trial: !!tenant.is_trial,
      trial_days: '',
    });
    setEditTenantOpen(true);
    if (editPlans.length === 0) {
      try {
        const { data } = await axios.get(`${ADMIN_API}/plans`, { headers });
        setEditPlans((data.plans || data || []).filter((p) => p.is_active !== false));
      } catch (err) {
        console.error('Failed to load plans:', err);
      }
    }
  };

  const handleSaveTenant = async () => {
    if (!editForm) return;
    setSavingTenant(true);
    // The trial is sent only when the operator changed it: the toggle moved, or
    // a new duration was typed while it is on. An unchanged trial must survive
    // an edit to an unrelated field untouched — sending `is_trial` every time
    // would move `trial_ends_at` forward on every save.
    const trialToggleChanged = editForm.is_trial !== !!tenant.is_trial;
    const trialDaysGiven = editForm.is_trial && editForm.trial_days !== '';
    const trialPatch = (trialToggleChanged || trialDaysGiven)
      ? {
          is_trial: editForm.is_trial,
          // Turning the trial OFF states no duration; the server clears the end
          // date. Turning it ON without one is refused by the server, by name,
          // where the operator can read why.
          ...(editForm.is_trial && editForm.trial_days !== ''
            ? { trial_days: Number(editForm.trial_days) }
            : {}),
        }
      : {};
    try {
      await axios.patch(`${ADMIN_API}/tenants/${tenantId}`, {
        tenant_name: editForm.tenant_name,
        organization_name: editForm.organization_name,
        industry: editForm.industry || null,
        region: editForm.region || null,
        seat_limit: editForm.seat_limit === '' ? undefined : Number(editForm.seat_limit),
        // Only send a plan if one was actually chosen. Sending '' would ask the
        // API to assign an empty plan; omitting it leaves the tenant's
        // commercial state exactly as it was, which is what an operator editing
        // a name intends.
        ...(editForm.plan ? { plan: editForm.plan } : {}),
        ...trialPatch,
      }, { headers });
      toast.success('Tenant updated');
      setEditTenantOpen(false);
      fetchTenantData();
    } catch (err) {
      console.error('Failed to update tenant:', err);
      toast.error(err.response?.data?.detail || 'Failed to update tenant');
    } finally {
      setSavingTenant(false);
    }
  };

  // Whether the tenant holds a Modules-tab row, by either store and either
  // spelling. Asked of a row AND of the product that includes it
  // (`includedWithName`, `showsAsOwnTile`).
  const holdsModuleRow = (m) => {
    const c = m.canonical_code || m.api_name;
    const entitlements = tenant?.module_entitlements || [];
    return modules.some((x) => (x.module_code === c || x.module_code === m.api_name)
                              && x.is_enabled === true)
      || entitlements.includes(c)
      || entitlements.includes(m.api_name);
  };

  // Toggle module. The control-plane route hands every code the Products
  // page lists to the same toggle that page uses (`toggle_tenant_module`),
  // so what a product grants — Engage's Forms and Surveys plumbing, its
  // licence at the tenant's CRM seat count, Assistants' Live Chat — moves
  // with it here too (closure audit 10-06, M1/M3).
  const handleToggleModule = async (moduleCode, isEnabled, name) => {
    // The product's NAME. This toast printed the code with underscores
    // swapped for spaces ("ai features enabled", "chatbot disabled").
    const label = name || productLabel(moduleCode, productNames);
    try {
      const endpoint = isEnabled
        ? `${CONTROL_PLANE_API}/tenants/${tenantId}/modules/${moduleCode}/disable`
        : `${CONTROL_PLANE_API}/tenants/${tenantId}/modules/${moduleCode}/enable`;
      const { data } = await axios.post(endpoint, {}, { headers });
      // The server reads the runtime back; "enabled" is only said when the
      // workspace will allow it.
      const rt = data?.runtime;
      if (!isEnabled && rt && rt.has_runtime_app && rt.live === false) {
        toast.error(`${label} was recorded, but is not live in the workspace yet`);
      } else if (isEnabled && rt && rt.has_runtime_app && rt.live === true) {
        toast.error(`${label} was recorded as disabled, but the workspace still allows it`);
      } else {
        // A product switched off takes its add-ons with it; a clear that
        // failed answers `completed_with_failures` with its remedy (A-2).
        const outcome = moduleToggleOutcome(data, { label, wasEnabled: isEnabled });
        if (outcome.tone === 'warning') toast(outcome.text, { icon: '⚠️', duration: 8000 });
        else toast.success(outcome.text);
      }
      fetchModules();
    } catch (err) {
      console.error('Failed to toggle module:', err);
      toast.error(err.response?.data?.detail || 'Failed to update module');
    }
  };

  /*
   * `handleRetryJob` USED TO BE HERE, and it posted to
   * `POST /provisioning/jobs/{job_id}/retry`.
   *
   * That route re-runs the SAME job row and never grants a fresh retry budget.
   * On a row that reached FAILED by consuming its budget it bought exactly one
   * attempt whose first failure is terminal immediately — no backoff ladder —
   * while looking to the operator like a fresh lifecycle. The backend now
   * answers that case with 409 naming Repair.
   *
   * The operator-facing action is Repair, which mints a NEW job row with a
   * fresh budget and reuses the frozen spec's provisioning_request_id. It lives
   * in `RepairProvisioningAction.jsx` — its own file, because this one imports
   * react-router-dom and therefore cannot be mounted by a test at all, and the
   * control that decides whether a tenant gets re-provisioned should not be the
   * one nothing can look at.
   *
   * It is not placed on the failed row, and it is not called Retry: the failed
   * attempt stays terminal and its retry_count is never carried into the new
   * row. `OnboardingPanel.jsx` made the same move for Sample Data; this matches
   * it rather than inventing a second pattern.
   */

  // Status badge helper
  const getStatusBadge = (status) => {
    // An absent status is not 'active'. Production holds tenants whose document
    // predates the field, and showing them a green Active badge asserts
    // something nobody established.
    const normalizedStatus = (status || '').toLowerCase();
    const config = {
      active: { color: 'bg-green-100 text-green-700 border-green-200', icon: CheckCircle },
      suspended: { color: 'bg-red-100 text-red-700 border-red-200', icon: XCircle },
      trial: { color: 'bg-blue-100 text-blue-700 border-blue-200', icon: Clock },
      pending: { color: 'bg-yellow-100 text-yellow-700 border-yellow-200', icon: Clock },
      provisioning: { color: 'bg-indigo-100 text-indigo-700 border-indigo-200', icon: Loader2 },
      read_only: { color: 'bg-orange-100 text-orange-700 border-orange-200', icon: AlertCircle },
      terminated: { color: 'bg-slate-100 text-slate-700 border-slate-200', icon: XCircle },
    };
    const unknown = { color: 'bg-slate-100 text-slate-500 border-slate-200', icon: AlertCircle };
    const { color, icon: Icon } = config[normalizedStatus] || unknown;
    return (
      <Badge variant="outline" className={`${color} flex items-center gap-1`}>
        <Icon className={`h-3 w-3 ${normalizedStatus === 'provisioning' ? 'animate-spin' : ''}`} />
        <span className="capitalize">{status || 'Unknown'}</span>
      </Badge>
    );
  };

  const getCommercialStateBadge = (state) => {
    const colors = {
      active_subscription: 'bg-emerald-100 text-emerald-700',
      plan: 'bg-purple-100 text-purple-700',
      unconfigured: 'bg-slate-100 text-slate-500',
    };
    const kind = state?.kind || 'unavailable';
    const label = state?.label || 'Commercial state unavailable';
    return <Badge className={colors[kind] || 'bg-slate-100 text-slate-600'}>{label}</Badge>;
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  if (error || !tenant) {
    return (
      <div className="p-6">
        <Card className="border-red-200 bg-red-50">
          <CardContent className="flex items-center gap-3 p-6">
            <AlertCircle className="h-6 w-6 text-red-600" />
            <div>
              <p className="font-medium text-red-900">{error || 'Tenant not found'}</p>
              <Button variant="link" className="p-0 h-auto text-red-600" onClick={() => navigate('/tenants')}>
                Back to Tenants
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/tenants')}
          className="mb-4 text-slate-600 hover:text-slate-900"
        >
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back to Tenants
        </Button>

        {/* Stacks below sm: at 390 the unwrapped row put Edit and Refresh at
            x=354..536, off a phone screen (T29/NX-23, S18 browser evidence). */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="flex min-w-0 items-center gap-4">
            <div className="w-16 h-16 shrink-0 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center shadow-lg">
              <span className="text-2xl text-white font-bold">
                {(tenant.tenant_name || tenant.company_name)?.[0] || 'T'}
              </span>
            </div>
            <div>
              <div className="flex flex-wrap items-center gap-3">
                <h1 className="min-w-0 break-words text-2xl font-bold text-slate-900">{tenant.tenant_name || tenant.company_name}</h1>
                {getStatusBadge(tenant.status)}
              </div>
              {/* The header used to stop at the lifecycle badge, which is how a
                  tenant read PROVISIONING here while the Provisioning tab on
                  the same screen read Ready. The badge KEEPS its authority —
                  it alone carries SUSPENDED, READ_ONLY and TERMINATED — and
                  this adds the progress the tab was already showing, from the
                  SAME projection, so the two surfaces cannot phrase it
                  differently. Renders nothing once the tenant is no longer
                  provisioning: a stale progress line beside a green badge is
                  the same contradiction wearing different clothes. */}
              {describeProgress(readiness?.progress) && (
                <p className={`text-sm mt-1 ${needsAttention(readiness?.progress) ? 'text-amber-700' : 'text-slate-600'}`}
                   data-testid="tenant-header-progress">
                  {describeProgress(readiness.progress)}
                </p>
              )}
              {/* THE GAP THE PROGRESS LINE LEAVES. `describeProgress` renders
                  nothing outside PROVISIONING, on purpose — so the two states
                  where the badge is confidently WRONG (Active over an unbuilt
                  baseline; Provisioning over a finished one) showed the badge
                  and nothing at all. The badge keeps its authority; this says,
                  in words, that the measured evidence contradicts it. Rendered
                  only when the backend positively detected the contradiction,
                  never when provisioning is simply unmeasured. */}
              {describeDrift(readiness?.drift) && (
                <p className="text-sm mt-1 flex items-start gap-1.5 text-amber-700"
                   data-testid="tenant-header-setup-drift">
                  <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
                  <span>{describeDrift(readiness.drift)}</span>
                </p>
              )}
              <p className="text-slate-500 mt-1">{tenant.organization_name || tenant.industry}</p>
              <div className="flex flex-wrap items-center gap-3 mt-2">
                {(() => {
                  const env = describeEnvironment(tenant.environment);
                  return (
                    <StatusBadge
                      status={env.code ? env.code.toLowerCase() : 'unclassified'}
                      label={env.code || env.label}
                      dot={false}
                      className="font-semibold tracking-wide"
                      data-testid="tenant-environment-badge"
                    />
                  );
                })()}
                {tenant.sample_credentials && (
                  <Badge variant="outline" className="border-indigo-200 bg-indigo-50 text-indigo-700" data-testid="tenant-sample-credentials-badge">
                    Demo Connections
                  </Badge>
                )}
                {getCommercialStateBadge(tenant.commercial_state)}
                <span className="text-sm text-slate-500">ID: {tenant.id.slice(0, 8)}...</span>
              </div>
            </div>
          </div>

          <div className="flex shrink-0 flex-wrap items-center gap-2">
            <PermissionGate permission="tenant:update">
              <Button variant="outline" size="sm" onClick={openEditTenant} data-testid="edit-tenant-btn">
                <Pencil className="h-4 w-4 mr-1" />
                Edit
              </Button>
            </PermissionGate>
            {/* REFRESH MUST REFRESH THE PROGRESS TOO. This reloaded the tenant
                document alone, so the header progress line kept whatever
                `readiness` held when the page mounted while the Overview panel
                — which refetches itself — moved on. Measured live on db626f43:
                the header read "Not measured · 29s elapsed" directly above a
                panel reading "10 of 20 steps · 50% · standard_objects · 48s
                elapsed", one tenant, one screen, two answers. The projection
                was right; both surfaces were reading it at different times. */}
            <Button variant="outline" size="sm"
                    onClick={() => { fetchTenantData(); fetchReadiness(); }}>
              <RefreshCw className="h-4 w-4 mr-1" />
              Refresh
            </Button>
          </div>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4 mb-6">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Users</p>
                <p className="text-2xl font-bold text-slate-900">
                  {tenant.current_users || 0}
                  <span className="text-sm font-normal text-slate-400"> / {tenant.seat_limit || 10}</span>
                </p>
              </div>
              <div className="p-3 bg-emerald-50 rounded-lg">
                <Users className="h-5 w-5 text-emerald-600" />
              </div>
            </div>
            <Progress 
              value={tenant.seat_limit ? ((tenant.current_users || 0) / tenant.seat_limit) * 100 : 0} 
              className="h-1.5 mt-3"
            />
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">File Manager recorded storage</p>
                {/* An unavailable measurement must never render as zero. */}
                {storageQuota?.current_usage == null ? (
                  <p className="text-lg font-semibold text-muted-foreground" title="File Manager usage could not be measured right now">
                    Not measured
                  </p>
                ) : (
                  <p className="text-2xl font-bold text-foreground">
                    {(storageQuota.current_usage / (1024 ** 3)).toFixed(1)}
                    <span className="text-sm font-normal text-muted-foreground"> GB</span>
                  </p>
                )}
                {storageQuota?.effective_limit != null && storageQuota.effective_limit >= 0 ? (
                  <p className="text-xs text-muted-foreground mt-0.5">
                    of {(storageQuota.effective_limit / (1024 ** 3)).toLocaleString()} GB allowance
                  </p>
                ) : null}
              </div>
              <div className="p-3 bg-primary/10 rounded-lg">
                <HardDrive className="h-5 w-5 text-primary" />
              </div>
            </div>
            {/* No bar when there is nothing measured to draw — an empty bar
                reads as "0% used", which is a measurement we do not have. */}
            {storageQuota?.current_usage != null && storageQuota.effective_limit > 0 ? (
              <Progress
                value={(storageQuota.current_usage / storageQuota.effective_limit) * 100}
                className="h-1.5 mt-3"
              />
            ) : null}
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Modules</p>
                <p className="text-2xl font-bold text-slate-900">
                  {(tenant.module_entitlements || []).length}
                </p>
              </div>
              <div className="p-3 bg-blue-50 rounded-lg">
                <Boxes className="h-5 w-5 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Created</p>
                <p className="text-lg font-bold text-slate-900">
                  {tenant.created_at ? new Date(tenant.created_at).toLocaleDateString() : 'N/A'}
                </p>
              </div>
              <div className="p-3 bg-amber-50 rounded-lg">
                <Calendar className="h-5 w-5 text-amber-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        {/* Radix gives TabsList a fixed h-9 by default. This tenant page has
            enough tabs to wrap, so the list must grow with its rows; otherwise
            wrapped triggers paint over the selected panel on narrow screens. */}
        <TabsList className="h-auto w-full justify-start gap-1 bg-slate-100 p-1 flex flex-wrap">
          <TabsTrigger value="overview" className="data-[state=active]:bg-white">
            <Building2 className="h-4 w-4 mr-2" />
            Overview
          </TabsTrigger>
          {/* Subscription and Billing were two tabs telling two stories about
              the same tenant, reading different endpoints. One surface now. */}
          <TabsTrigger value="subscription" className="data-[state=active]:bg-white">
            <CreditCard className="h-4 w-4 mr-2" />
            Subscription &amp; Billing
          </TabsTrigger>
          <TabsTrigger value="licenses" className="data-[state=active]:bg-white">
            <Key className="h-4 w-4 mr-2" />
            Licenses
          </TabsTrigger>
          <TabsTrigger value="modules" className="data-[state=active]:bg-white">
            <Boxes className="h-4 w-4 mr-2" />
            Modules
          </TabsTrigger>
          <TabsTrigger value="version" className="data-[state=active]:bg-white">
            <Rocket className="h-4 w-4 mr-2" />
            Version
          </TabsTrigger>
          <TabsTrigger value="limits" className="data-[state=active]:bg-white">
            <BarChart3 className="h-4 w-4 mr-2" />
            Products &amp; Limits
          </TabsTrigger>
          <TabsTrigger value="users" className="data-[state=active]:bg-white">
            <Users className="h-4 w-4 mr-2" />
            Users
          </TabsTrigger>
          <TabsTrigger value="emails" className="data-[state=active]:bg-white">
            <Mail className="h-4 w-4 mr-2" />
            Emails
          </TabsTrigger>
          <TabsTrigger value="provisioning" className="data-[state=active]:bg-white">
            <Server className="h-4 w-4 mr-2" />
            Provisioning
          </TabsTrigger>
          <TabsTrigger value="audit" className="data-[state=active]:bg-white">
            <History className="h-4 w-4 mr-2" />
            Audit
          </TabsTrigger>
        </TabsList>

        {/* OVERVIEW TAB */}
        <TabsContent value="overview">
          {/* Onboarding first: "can this customer get in?" is the question an
              operator opens a new tenant to answer, and its controls used to
              be spread across three tabs with no answer for the invitation at
              all. */}
          <div className="mb-6">
            <OnboardingPanel tenantId={tenantId} tenantName={tenant?.tenant_name || tenant?.company_name} headers={headers} can={can} />
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Building2 className="h-4 w-4" />
                  Organization Details
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex justify-between items-center py-2 border-b">
                  <span className="text-slate-500">Company Name</span>
                  <span className="font-medium">{tenant.tenant_name || tenant.company_name}</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b">
                  <span className="text-slate-500">Organization</span>
                  <span className="font-medium">{tenant.organization_name || '-'}</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b">
                  <span className="text-slate-500">Industry</span>
                  <span className="font-medium capitalize">{tenant.industry || 'Not specified'}</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b">
                  <span className="text-slate-500">Subdomain</span>
                  <span className="font-medium font-mono">{tenant.subdomain || '-'}</span>
                </div>
                <div className="flex justify-between items-center py-2">
                  <span className="text-slate-500">Region</span>
                  <span className="font-medium">{tenant.region || 'Default'}</span>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Shield className="h-4 w-4" />
                  Admin Contact
                </CardTitle>
              </CardHeader>
              <CardContent>
                {tenant.admin_user ? (
                  <div className="space-y-4">
                    <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                      <div className="w-12 h-12 bg-indigo-100 rounded-full flex items-center justify-center">
                        <span className="text-indigo-700 font-medium text-lg">
                          {tenant.admin_user.first_name?.[0]}{tenant.admin_user.last_name?.[0]}
                        </span>
                      </div>
                      <div>
                        <p className="font-medium">{tenant.admin_user.first_name} {tenant.admin_user.last_name}</p>
                        <p className="text-sm text-slate-500">{tenant.admin_user.email}</p>
                      </div>
                    </div>
                    <div className="flex justify-between items-center py-2 border-b">
                      <span className="text-slate-500">Access</span>
                      {/* `|| 'Admin'` printed "Admin" for every tenant, because
                          a provisioned admin correctly carries no role at all. */}
                      <Badge className="bg-indigo-100 text-indigo-700 hover:bg-indigo-100">
                        System Administrator
                      </Badge>
                    </div>
                    <div className="flex justify-between items-center py-2">
                      <span className="text-slate-500">Last Login</span>
                      <span className="text-sm">{tenant.admin_user.last_login ? new Date(tenant.admin_user.last_login).toLocaleString() : 'Never'}</span>
                    </div>
                  </div>
                ) : (
                  <p className="text-slate-500 text-center py-4">No admin user assigned</p>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Lifecycle administration.
              These lived on a "Support" tab that was a grab-bag of buttons
              rather than an operator task. Suspending or terminating a tenant
              is administration of THIS tenant, so it belongs beside it.
              Every action is offered only where the lifecycle contract allows
              it — see tenantLifecycle.js; the backend refuses the rest with
              409 regardless. */}
          <Card className="mt-6 border-slate-200">
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Wrench className="h-4 w-4" />
                Lifecycle administration
              </CardTitle>
              <CardDescription>
                Suspension takes effect immediately across the platform.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {terminalNotice(tenant.status) && (
                <p className="text-sm text-slate-500" data-testid="lifecycle-terminal-notice">
                  {terminalNotice(tenant.status)}
                </p>
              )}
              {!terminalNotice(tenant.status) && (
                <>
                  <div className="space-y-2">
                    <Label>Reason (optional)</Label>
                    <Textarea
                      value={supportReason}
                      onChange={(e) => setSupportReason(e.target.value)}
                      placeholder="Recorded on the audit entry for this action…"
                      data-testid="lifecycle-reason"
                    />
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {canSuspend(tenant.status) && (
                      <PermissionGate permission="tenant:suspend">
                        <Button variant="outline" className="text-amber-600 border-amber-200"
                                onClick={() => handleSupportAction('SUSPEND')}
                                disabled={actionLoading} data-testid="lifecycle-suspend">
                          <Pause className="h-4 w-4 mr-2" /> Suspend
                        </Button>
                      </PermissionGate>
                    )}
                    {canReactivate(tenant.status) && (
                      <PermissionGate permission="tenant:suspend">
                        <Button variant="outline" className="text-green-600 border-green-200"
                                onClick={() => handleSupportAction('REACTIVATE')}
                                disabled={actionLoading} data-testid="lifecycle-reactivate">
                          <Play className="h-4 w-4 mr-2" /> Reactivate
                        </Button>
                      </PermissionGate>
                    )}
                    {canSetReadOnly(tenant.status) && (
                      <PermissionGate permission="tenant:suspend">
                        <Button variant="outline" className="text-orange-600 border-orange-200"
                                onClick={() => handleSupportAction('SET_READ_ONLY')}
                                disabled={actionLoading} data-testid="lifecycle-readonly">
                          <EyeOff className="h-4 w-4 mr-2" /> Read-only
                        </Button>
                      </PermissionGate>
                    )}
                    <PermissionGate permission="tenant:update">
                      <Button variant="outline" onClick={() => handleSupportAction('RESEND_WELCOME')}
                              disabled={actionLoading} data-testid="lifecycle-resend-welcome">
                        <Send className="h-4 w-4 mr-2" /> Resend welcome
                      </Button>
                    </PermissionGate>
                    {/* Offered at all only where the lifecycle contract allows
                        it. Inside that: Owner only, after typing the tenant's
                        name and a reason (D3 hotfix, Option B); anyone else
                        sees it disabled with the reason, reachable by keyboard
                        and screen reader. */}
                    {canTerminate(tenant.status) && (
                      <TerminateTenantControl status={tenant.status} operator={adminUser}
                                              tenantName={tenant.tenant_name || tenant.company_name}
                                              busy={actionLoading}
                                              onTerminate={handleTerminateTenant} />
                    )}
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* SUBSCRIPTION TAB */}
        <TabsContent value="subscription">
          {/* One surface. The previous Subscription tab rendered fields off
              the raw tenant document while Billing read a computed summary,
              so the two disagreed about the same tenant. */}
          <SubscriptionBillingTab tenantId={tenantId} />
        </TabsContent>

        {/* MODULES TAB */}
        <TabsContent value="modules">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-base flex items-center gap-2">
                    <Boxes className="h-4 w-4" />
                    Module Entitlements
                  </CardTitle>
                  <CardDescription>
                    Enable or disable runtime product entitlements for this tenant.
                    Commercial state: <Badge variant="outline" className="ml-1">
                      {tenant.commercial_state?.label || 'Unavailable'}
                    </Badge>
                  </CardDescription>
                </div>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={async () => {
                    try {
                      setActionLoading(true);
                      await axios.post(
                        `${CONTROL_PLANE_API}/tenants/${tenantId}/plan/sync`,
                        {},
                        { headers }
                      );
                      await fetchModules();
                      await fetchTenantData();
                    } catch (err) {
                      console.error('Sync failed:', err);
                    } finally {
                      setActionLoading(false);
                    }
                  }}
                  disabled={actionLoading || !tenant.plan}
                  title={tenant.plan ? 'Re-apply this plan to runtime entitlements' : 'No legacy plan is assigned; direct agreements are managed in Subscription & Billing'}
                >
                  <RotateCcw className={`h-4 w-4 mr-2 ${actionLoading ? 'animate-spin' : ''}`} />
                  Sync with Plan
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {availableModules.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <Loader2 className="h-8 w-8 animate-spin mx-auto mb-2" />
                  <p>Loading modules...</p>
                </div>
              ) : (
                <>
                  {/* WHAT THE WORKSPACE WILL ALLOW, before the cards. A product
                      recorded here that never reached the runtime is named,
                      with the same retry Create Tenant offers. */}
                  {productStateError && (
                    <p className="mb-4 text-xs text-warning-text" data-testid="product-state-error">
                      {productStateError}
                    </p>
                  )}
                  {productState && productState.not_landed?.length > 0 && (
                    <div className="mb-4 rounded-md border border-warning/30 bg-warning/10 p-3"
                         data-testid="products-not-landed">
                      <p className="text-sm font-medium text-warning-text">
                        Not live in the workspace: {productState.not_landed
                          .map((c) => productLabel(c, productNames)).join(', ')}
                      </p>
                      <p className="mt-1 text-xs text-muted-foreground">
                        These are recorded for the tenant, but the workspace refuses
                        them until they are switched on there.
                      </p>
                      <Button variant="outline" size="sm" className="mt-2"
                              onClick={handleReapplyProducts} disabled={reapplying}
                              data-testid="products-reapply">
                        {reapplying ? 'Switching on…' : 'Switch on in workspace'}
                      </Button>
                    </div>
                  )}
                  {reapplyResult && !reapplyResult.ok && (
                    <div className="mb-4">
                      <ProductSetupOutcome setup={reapplyResult} />
                    </div>
                  )}

                  {/* Group modules by category */}
                  {Object.entries(
                    // A legacy alias row (`crm`, marked `duplicate_of`) is the
                    // same product as its canonical row; showing both offered
                    // an OFF switch for a product the tenant has.
                    //
                    // ONE ENGAGE (closure audit 10-06, M6): a product retired
                    // into another — Form Builder and Survey Builder, into
                    // Engage — gets no tile beside its replacement. Only a
                    // tenant holding it WITHOUT Engage sees it, with a switch
                    // to turn it off. See `showsAsOwnTile`.
                    availableModules.filter((m) => showsAsOwnTile(m, availableModules, holdsModuleRow)).reduce((groups, module) => {
                      const cat = module.category || 'other';
                      if (!groups[cat]) groups[cat] = [];
                      groups[cat].push(module);
                      return groups;
                    }, {})
                  ).sort(([a], [b]) => {
                    const order = ['core', 'productivity', 'admin', 'automation', 'data', 'engagement', 'ai', 'advanced', 'analytics', 'config'];
                    return order.indexOf(a) - order.indexOf(b);
                  }).map(([category, categoryModules]) => (
                    <div key={category} className="mb-6">
                      <h4 className="text-sm font-semibold text-slate-700 uppercase tracking-wider mb-3 capitalize">
                        {category}
                      </h4>
                      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {categoryModules.map((module, idx) => {
                          // Compare the CANONICAL code the tenant holds, not the
                          // page's spelling: Assistants is listed as
                          // `chatbot_manager` and held as `chatbot`.
                          const code = module.canonical_code || module.api_name;
                          const dbModule = modules.find(m => m.module_code === code || m.module_code === module.api_name);
                          // Check if module is enabled via: 1) tenant_modules collection, 2) tenant.module_entitlements
                          const tenantEntitlements = tenant?.module_entitlements || [];
                          const isEnabled = dbModule?.is_enabled === true
                            || tenantEntitlements.includes(code)
                            || tenantEntitlements.includes(module.api_name);
                          const includedWith = includedWithName(module, availableModules, holdsModuleRow);
                          const runtime = (productState?.products || [])
                            .find((p) => p.app_code === code) || null;
                          const name = productRows[code]?.label || productNames[code]
                            || module.name || productLabel(code, productNames);

                          return (
                            <TenantProductCard
                              key={module.api_name || `idx-${idx}`}
                              code={code}
                              module={module}
                              row={productRows[code] || null}
                              labels={productNames}
                              enabled={isEnabled}
                              includedWith={includedWith}
                              runtime={runtime}
                              enabledSource={dbModule?.enabled_source}
                              onToggle={() => handleToggleModule(code, isEnabled, name)}
                              onReapply={handleReapplyProducts}
                              reapplying={reapplying}
                              onRecheck={recheckProductPrices}
                              rechecking={priceRechecking}
                            />
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* LICENSES TAB */}
        <TabsContent value="licenses">
          <TenantLicensesTab tenantId={tenantId} getAdminToken={getAdminToken} />
        </TabsContent>

        {/* BILLING TAB */}

        {/* VERSION TAB */}
        <TabsContent value="version">
          <TenantVersionTab tenantId={tenantId} getAdminToken={getAdminToken} />
        </TabsContent>

        {/* LIMITS TAB */}
        <TabsContent value="limits" className="space-y-4">
          <TenantProductsLimits
            tenantId={tenantId}
            tenantName={tenant?.tenant_name || tenant?.company_name}
            onManageEntitlements={() => setActiveTab('modules')}
          />
        </TabsContent>

        {/* USERS TAB */}
        <TabsContent value="users">
          <Card>
            <CardHeader className="flex flex-row items-start justify-between gap-4 space-y-0">
              <div>
                <CardTitle className="text-base flex items-center gap-2">
                  <Users className="h-4 w-4" />
                  Tenant Users ({users.length})
                </CardTitle>
                <CardDescription>
                  Users with pending password setup will show a reset link you can copy and share
                </CardDescription>
              </div>
              <Button
                size="sm"
                onClick={() => setCreateUserOpen(true)}
                data-testid="add-user-button"
              >
                <UserPlus className="h-4 w-4 mr-2" />
                Add User
              </Button>
            </CardHeader>
            <CardContent>
              {users.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>User</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Role</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Password Setup</TableHead>
                      <TableHead>Last Login</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {users.map((user) => {
                      const rowBusy = busyUserId === user.id;
                      return (
                      <TableRow key={user.id}>
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 bg-indigo-100 rounded-full flex items-center justify-center">
                              <span className="text-indigo-700 font-medium text-sm">
                                {user.first_name?.[0]}{user.last_name?.[0]}
                              </span>
                            </div>
                            <span className="font-medium">{user.first_name} {user.last_name}</span>
                          </div>
                        </TableCell>
                        <TableCell className="text-slate-500">{user.email}</TableCell>
                        <TableCell>
                          <div className="flex flex-col gap-1">
                            {/* Effective access first — it is the fact that
                                matters and the one the portal used to hide. */}
                            {user.is_system_administrator && (
                              <Badge className="bg-indigo-100 text-indigo-700 hover:bg-indigo-100 w-fit">
                                System Administrator
                              </Badge>
                            )}
                            {user.role_name ? (
                              <span className="text-xs text-slate-600">{user.role_name}</span>
                            ) : (
                              <span className="text-xs text-slate-400">No hierarchy role assigned</span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          {user.is_active ? (
                            <Badge className="bg-green-100 text-green-700">Active</Badge>
                          ) : (
                            <Badge className="bg-red-100 text-red-700">Inactive</Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          {user.password_setup_pending ? (
                            <PasswordSetupCell user={user} headers={headers} />
                          ) : user.must_change_password ? (
                            <Badge className="bg-orange-100 text-orange-700">Must Change</Badge>
                          ) : (
                            <Badge className="bg-green-100 text-green-700">Completed</Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-sm text-slate-500">
                          {user.last_login ? new Date(user.last_login).toLocaleString() : 'Never'}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center justify-end gap-1">
                            <PermissionGate permission="user:reset_password">
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-8 w-8 p-0"
                                title="Reset password"
                                disabled={rowBusy}
                                onClick={() => openResetPassword(user)}
                                data-testid={`reset-password-${user.id}`}
                              >
                                <Key className="h-4 w-4 text-slate-500" />
                              </Button>
                            </PermissionGate>
                            <PermissionGate permission="user:update">
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-8 w-8 p-0"
                                title="Change role"
                                disabled={rowBusy}
                                onClick={() => openAssignRole(user)}
                                data-testid={`role-icon-${user.id}`}
                              >
                                <UserCog className="h-4 w-4 text-slate-500" />
                              </Button>
                            </PermissionGate>
                            <PermissionGate permission="tenant:impersonate">
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-8 w-8 p-0"
                                title="Login as user — open the CRM as this user"
                                disabled={rowBusy || !user.is_active}
                                onClick={() => handleImpersonateUser(user)}
                                data-testid={`impersonate-user-${user.id}`}
                              >
                                <LogIn className="h-4 w-4 text-slate-500" />
                              </Button>
                            </PermissionGate>
                            {user.is_active ? (
                              <AlertDialog>
                                <AlertDialogTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-8 w-8 p-0"
                                    title="Suspend user"
                                    disabled={rowBusy}
                                    data-testid={`suspend-user-${user.id}`}
                                  >
                                    {rowBusy ? (
                                      <Loader2 className="h-4 w-4 animate-spin text-slate-500" />
                                    ) : (
                                      <Pause className="h-4 w-4 text-amber-600" />
                                    )}
                                  </Button>
                                </AlertDialogTrigger>
                                <AlertDialogContent>
                                  <AlertDialogHeader>
                                    <AlertDialogTitle>Suspend {user.first_name} {user.last_name}?</AlertDialogTitle>
                                    <AlertDialogDescription>
                                      They will not be able to log in until reactivated. This does not delete their data.
                                    </AlertDialogDescription>
                                  </AlertDialogHeader>
                                  <AlertDialogFooter>
                                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                                    <AlertDialogAction
                                      onClick={() => handleSuspendUser(user.id)}
                                      className="bg-amber-600"
                                    >
                                      Suspend
                                    </AlertDialogAction>
                                  </AlertDialogFooter>
                                </AlertDialogContent>
                              </AlertDialog>
                            ) : (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-8 w-8 p-0"
                                title="Activate user"
                                disabled={rowBusy}
                                onClick={() => handleActivateUser(user.id)}
                                data-testid={`activate-user-${user.id}`}
                              >
                                {rowBusy ? (
                                  <Loader2 className="h-4 w-4 animate-spin text-slate-500" />
                                ) : (
                                  <Play className="h-4 w-4 text-green-600" />
                                )}
                              </Button>
                            )}
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-8 w-8 p-0"
                                  title="Delete user"
                                  disabled={rowBusy}
                                  data-testid={`delete-user-${user.id}`}
                                >
                                  <Trash2 className="h-4 w-4 text-red-600" />
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>Delete {user.first_name} {user.last_name}?</AlertDialogTitle>
                                  <AlertDialogDescription>
                                    This permanently deletes the user account ({user.email}). This cannot be undone.
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                                  <AlertDialogAction
                                    onClick={() => handleDeleteUser(user.id)}
                                    className="bg-red-600"
                                  >
                                    Delete
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          </div>
                        </TableCell>
                      </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              ) : (
                <div className="text-center py-8 text-slate-500">
                  <Users className="h-10 w-10 mx-auto mb-2 text-slate-300" />
                  <p>No users found</p>
                  <Button
                    size="sm"
                    variant="outline"
                    className="mt-3"
                    onClick={() => setCreateUserOpen(true)}
                  >
                    <UserPlus className="h-4 w-4 mr-2" />
                    Add the first user
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* EMAILS TAB */}
        <TabsContent value="emails">
          <TenantEmailsTab tenantId={tenantId} getAdminToken={getAdminToken} />
        </TabsContent>

        {/* PROVISIONING TAB */}
        <TabsContent value="provisioning">
          {/* Desired vs attempted vs actual, from the reconciled route. Rendered
              for every tenant, not only those with sample_credentials on: "this
              tenant never asked" is one of the answers the route gives, and it is
              a different fact from the card not being there. */}
          {/* `tenant` carries the DESIRED decision (`sample_credentials`,
              the profile id, the environment) and `managed_services` from the
              detail read; the card reads ACTUAL state itself. After a switch
              both are re-read so the header badge and the card agree. */}
          <DemoConnectionsCard
            tenantId={tenantId}
            tenant={tenant}
            onTenantChanged={fetchTenantData}
            notify={toast}
            canEdit={demoSwitchPermission.allowed}
            permissionReason={demoSwitchPermission.reason}
          />
          {/* Runtime readiness — this, not Tenant.status, is what "Ready" means */}
          <Card className="mb-4">
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Server className="h-4 w-4" />
                Runtime Readiness
                {readiness && (
                  <Badge className={`ml-2 ${{
                    ready: 'bg-green-100 text-green-700 border-green-200',
                    provisioning: 'bg-blue-100 text-blue-700 border-blue-200',
                    attention: 'bg-red-100 text-red-700 border-red-200',
                    incomplete: 'bg-amber-100 text-amber-700 border-amber-200',
                    // Slate, not amber: nothing here is actionable.
                    legacy: 'bg-slate-100 text-slate-600 border-slate-200',
                    unknown: 'bg-slate-100 text-slate-600 border-slate-200',
                  }[readiness.state] || 'bg-slate-100 text-slate-600'}`}>
                    {readiness.state === 'provisioning' && <Loader2 className="h-3 w-3 mr-1 animate-spin" />}
                    {{
                      ready: 'Ready',
                      provisioning: 'Being prepared',
                      attention: 'Needs attention',
                      incomplete: 'Incomplete',
                      legacy: 'Legacy tenant',
                      unknown: 'Unknown',
                    }[readiness.state] || readiness.state}
                  </Badge>
                )}
              </CardTitle>
              <CardDescription>
                Derived from the provisioning report — a tenant is Ready only when every
                required step is positively verified, never from its lifecycle status alone.
              </CardDescription>
              {/* The same sentence the header and the Overview panel show, from
                  the same projection. This tab already had the step detail
                  below; what it lacked was the one-line summary the other
                  surfaces were each phrasing for themselves. */}
              {describeProgress(readiness?.progress) && (
                <p className="text-sm text-slate-600 mt-2" data-testid="provisioning-tab-progress">
                  {describeProgress(readiness.progress)}
                </p>
              )}
            </CardHeader>
            <CardContent>
              {readinessLoading && !readiness ? (
                <div className="flex items-center gap-2 text-slate-500 text-sm py-2">
                  <Loader2 className="h-4 w-4 animate-spin" /> Checking runtime state…
                </div>
              ) : !readiness ? (
                <p className="text-sm text-slate-500">Readiness could not be loaded.</p>
              ) : (
                <div className="space-y-3">
                  {readiness.verification_error && (
                    <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1">
                      {readiness.verification_error}
                    </p>
                  )}
                  {readiness.job?.error_message && readiness.state === 'attention' && (
                    <p className="text-xs text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1 break-words">
                      Last attempt: {readiness.job.error_message.split('\n')[0]}
                    </p>
                  )}
                  {(() => {
                    const steps = [
                      ...(readiness.report?.action_steps || []),
                      ...(readiness.report?.verification_steps
                          || readiness.verification?.verification_steps || []),
                    ];
                    if (steps.length === 0) {
                      return <p className="text-sm text-slate-500">No per-step report available yet.</p>;
                    }
                    const stepColor = {
                      complete: 'bg-green-100 text-green-700',
                      incomplete: 'bg-amber-100 text-amber-700',
                      legacy: 'bg-slate-100 text-slate-600',
                      failed: 'bg-red-100 text-red-700',
                      not_run: 'bg-slate-100 text-slate-500',
                    };
                    return (
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Step</TableHead>
                            <TableHead>Status</TableHead>
                            <TableHead>Detail</TableHead>
                            <TableHead>Evidence</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {steps.map((s, i) => (
                            <TableRow key={`${s.step}-${i}`}>
                              {/* THE STAGE IN WORDS, and the slug kept where an
                                  engineer can still find it. This cell held the
                                  raw slug: an operator read `read_only_permission_set`
                                  in a status table. The mapping in
                                  `provisioningStages` is the only place a slug
                                  becomes a phrase, so this table and the
                                  progress sentence cannot say different things
                                  about the same step. A slug from a newer
                                  Platform still renders — humanised, and
                                  flagged so nobody reads the fallback as a
                                  defined stage name. */}
                              <TableCell className="font-medium text-sm">
                                {(() => {
                                  const stage = describeStage(s.step);
                                  if (!stage) return <span className="text-muted-foreground">—</span>;
                                  return (
                                    <>
                                      <span>{stage.label}</span>
                                      {!stage.known && (
                                        <span
                                          className="ml-1.5 text-xs text-muted-foreground"
                                          title="This Admin build has no name for this step. It comes from a newer Platform release."
                                        >
                                          (newer platform step)
                                        </span>
                                      )}
                                      <span className="block font-mono text-[11px] text-muted-foreground">
                                        {stage.slug}
                                      </span>
                                    </>
                                  );
                                })()}
                              </TableCell>
                              <TableCell>
                                <Badge className={stepColor[s.status] || 'bg-slate-100 text-slate-600'}>
                                  {s.status}
                                </Badge>
                                {s.bundle && s.status === 'not_run' && (
                                  <span className="ml-1 text-xs text-slate-400">({s.bundle})</span>
                                )}
                              </TableCell>
                              <TableCell className="text-xs text-slate-500 max-w-md break-words">
                                {s.detail || '-'}
                              </TableCell>
                              <TableCell className="text-xs text-slate-500">
                                {evidenceRows(s.evidence).length > 0 ? (
                                  <dl className="space-y-1" data-testid={`provisioning-evidence-${s.step}`}>
                                    {evidenceRows(s.evidence).map((row) => (
                                      <div key={row.key} className="grid grid-cols-[minmax(7rem,auto)_1fr] gap-2">
                                        <dt className="font-medium text-slate-600 capitalize">
                                          {evidenceKeyLabel(row.key)}
                                        </dt>
                                        <dd className="font-mono break-all">{row.value}</dd>
                                      </div>
                                    ))}
                                  </dl>
                                ) : '-'}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    );
                  })()}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Repair Setup — the write action. Separate from Refresh on purpose. */}
          <Card className="mb-4">
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Server className="h-4 w-4" />
                Repair Setup
              </CardTitle>
              <CardDescription>
                Re-runs the same platform baseline this tenant was created with, against
                this tenant. It reuses the approved configuration and only fills in what
                is missing — it never creates a second tenant and never touches custom
                objects, fields, layouts or records.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {!repairPreview ? (
                <p className="text-sm text-slate-500">Checking what this tenant is missing…</p>
              ) : (
                <div className="space-y-3">
                  {!repairPreview.measured && (
                    <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1">
                      The baseline could not be measured, so this list may be incomplete.
                    </p>
                  )}

                  {repairPreview.missing_count === 0 ? (
                    <p className="text-sm text-green-700">
                      Nothing missing — {repairPreview.healthy_count} required step
                      {repairPreview.healthy_count === 1 ? '' : 's'} verified. Repair would
                      be a no-op.
                    </p>
                  ) : (
                    <div>
                      <p className="text-sm font-medium text-slate-700 mb-1">
                        Missing ({repairPreview.missing_count})
                      </p>
                      <ul className="text-sm text-slate-600 list-disc pl-5 space-y-0.5">
                        {repairPreview.missing.map((m) => (
                          <li key={m.step}>
                            {/* Named, then identified. A list of what is MISSING
                                is the place an operator most needs to know what
                                the thing IS; the slug stays beside it because
                                this list is also what gets pasted into a bug
                                report. */}
                            <span>{describeStage(m.step)?.label ?? '—'}</span>
                            <span className="ml-1.5 font-mono text-xs text-muted-foreground">{m.step}</span>
                            {m.detail ? <span className="text-slate-500"> — {m.detail}</span> : null}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {repairPreview.sample_data_requested !== null && (
                    <p className="text-xs text-slate-500">
                      Sample data for this tenant:{' '}
                      <strong>{repairPreview.sample_data_requested ? 'requested' : 'not requested'}</strong>
                      {' '}— repair follows the approved configuration, not today's defaults.
                    </p>
                  )}

                  <details className="text-xs text-slate-500">
                    <summary className="cursor-pointer">What repair will not touch</summary>
                    <ul className="list-disc pl-5 mt-1 space-y-0.5">
                      {repairPreview.preserved.map((x) => <li key={x}>{x}</li>)}
                    </ul>
                  </details>

                  {repairPreview.blocked_reason && (
                    <p className="text-xs text-slate-600 bg-slate-50 border border-slate-200 rounded px-2 py-1">
                      {repairPreview.blocked_reason}
                    </p>
                  )}

                  {repairResult && (
                    <p className={`text-xs rounded px-2 py-1 border ${repairResult.ok
                      ? 'text-green-700 bg-green-50 border-green-200'
                      : 'text-red-700 bg-red-50 border-red-200'}`}>
                      {repairResult.ok
                        ? 'Repair started. It runs in the background — the '
                          + 'Provisioning Jobs table below shows the outcome.'
                        : repairResult.message}
                    </p>
                  )}

                  <Button
                    onClick={runRepairSetup}
                    disabled={repairing || !repairPreview.repair_available || !can('tenant:update')}
                    data-testid="repair-setup-button"
                  >
                    {repairing
                      ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Repairing…</>
                      : 'Repair Setup'}
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Server className="h-4 w-4" />
                Provisioning Jobs
              </CardTitle>
              <CardDescription>Environment provisioning and modification history</CardDescription>
            </CardHeader>
            <CardContent>
              {provisioningJobs.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Job Type</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Requested By</TableHead>
                      <TableHead>Started</TableHead>
                      <TableHead>Completed</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {provisioningJobs.map((job) => (
                      <TableRow key={job.id}>
                        <TableCell className="font-medium">{job.job_type}</TableCell>
                        <TableCell>
                          <Badge className={`
                            ${job.status === 'COMPLETED' ? 'bg-green-100 text-green-700' : ''}
                            ${job.status === 'RUNNING' ? 'bg-blue-100 text-blue-700' : ''}
                            ${job.status === 'QUEUED' ? 'bg-yellow-100 text-yellow-700' : ''}
                            ${job.status === 'FAILED' ? 'bg-red-100 text-red-700' : ''}
                          `}>
                            {job.status === 'RUNNING' && <Loader2 className="h-3 w-3 mr-1 animate-spin" />}
                            {job.status}
                          </Badge>
                          {job.status === 'FAILED' && job.error_message && (
                            <p className="mt-1 max-w-xs truncate text-xs text-red-600"
                               title={job.error_message}>
                              {job.error_message.split('\n')[0]}
                            </p>
                          )}
                        </TableCell>
                        <TableCell className="text-slate-500">{job.requested_by || 'System'}</TableCell>
                        <TableCell className="text-sm text-slate-500">
                          {job.started_at ? new Date(job.started_at).toLocaleString() : '-'}
                        </TableCell>
                        <TableCell className="text-sm text-slate-500">
                          {job.completed_at ? new Date(job.completed_at).toLocaleString() : '-'}
                        </TableCell>
                        <TableCell>
                          {/* No per-row action. Re-running a terminal row is
                              not an operation this product offers; Repair
                              mints a new job and lives below the table, where
                              it cannot read as "continue this attempt". */}
                          {job.status === 'FAILED' && (
                            <span className="text-xs text-muted-foreground">
                              Terminal — use Repair below
                            </span>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <div className="text-center py-8 text-slate-500">
                  <Server className="h-10 w-10 mx-auto mb-2 text-slate-300" />
                  <p>No provisioning jobs</p>
                </div>
              )}

              {/* Repair sits under the history rather than inside a row: it
                  starts a NEW job against the frozen specification, and a
                  control on the failed row would say it continues that one. */}
              <PermissionGate permission="provisioning:execute">
                <div className="mt-4 border-t border-border pt-4">
                  <RepairProvisioningAction
                    tenantId={tenantId}
                    api={ADMIN_API}
                    headers={headers}
                    onStarted={fetchProvisioningJobs}
                    notify={toast}
                  />
                </div>
              </PermissionGate>
            </CardContent>
          </Card>
        </TabsContent>

        {/* AUDIT TAB */}
        <TabsContent value="audit">
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <History className="h-4 w-4" />
                Audit Log
              </CardTitle>
              <CardDescription>Recent changes and actions</CardDescription>
            </CardHeader>
            <CardContent>
              {/* FILTERS GO TO THE SERVER. Filtering a page of results in React
                  would mean "matches, among the first 25", which reads as
                  "these are all of them". */}
              <div className="flex flex-wrap gap-2 mb-4" data-testid="audit-filters">
                <Input
                  className="w-56"
                  placeholder="Search action, actor, entity, reason"
                  value={auditFilters.search}
                  onChange={(e) => setAuditFilters({ ...auditFilters, search: e.target.value })}
                  onKeyDown={(e) => { if (e.key === 'Enter') fetchAuditLogs(0, auditFilters); }}
                />
                <Input
                  className="w-48"
                  placeholder="Action (e.g. tenant_activated)"
                  value={auditFilters.action}
                  onChange={(e) => setAuditFilters({ ...auditFilters, action: e.target.value })}
                  onKeyDown={(e) => { if (e.key === 'Enter') fetchAuditLogs(0, auditFilters); }}
                />
                <Button variant="outline" size="sm" onClick={() => fetchAuditLogs(0, auditFilters)}>
                  Apply
                </Button>
                {(auditFilters.search || auditFilters.action || auditFilters.result) && (
                  <Button variant="ghost" size="sm" onClick={() => {
                    const cleared = { action: '', result: '', search: '' };
                    setAuditFilters(cleared); fetchAuditLogs(0, cleared);
                  }}>Clear</Button>
                )}
              </div>

              {(() => {
                const view = resolveAuditView({
                  loading: auditLoading, error: auditError, events: auditLogs });
                if (view === AUDIT_VIEW.LOADING) {
                  return (
                    <div className="text-center py-8 text-slate-500" data-testid="audit-loading">
                      <Loader2 className="h-6 w-6 mx-auto mb-2 animate-spin text-slate-300" />
                      <p>Loading audit history…</p>
                    </div>
                  );
                }
                if (view === AUDIT_VIEW.ERROR) {
                  // NOT "no audit logs found". Absence of events is evidence on
                  // this surface, so a failed read must never impersonate one.
                  return (
                    <div className="text-center py-8" data-testid="audit-error">
                      <AlertCircle className="h-10 w-10 mx-auto mb-2 text-red-300" />
                      <p className="text-red-700">{auditError}</p>
                      <p className="text-sm text-slate-500 mt-1">
                        This is not the same as the tenant having no history — nothing has been changed.
                      </p>
                      <Button variant="outline" size="sm" className="mt-3"
                              onClick={() => fetchAuditLogs(auditSkip, auditFilters)}>
                        <RefreshCw className="h-4 w-4 mr-2" />Retry
                      </Button>
                    </div>
                  );
                }
                if (view === AUDIT_VIEW.EMPTY) {
                  return (
                    <div className="text-center py-8 text-slate-500" data-testid="audit-empty">
                      <History className="h-10 w-10 mx-auto mb-2 text-slate-300" />
                      <p>No audit events match this view</p>
                    </div>
                  );
                }
                return (
                  <div className="space-y-2" data-testid="audit-list">
                    {auditLogs.map((log) => {
                      const s = summaryOf(log);
                      const open = expandedAudit === log.id;
                      const imp = impersonationOf(log);
                      return (
                        <div key={log.id} className="border rounded-lg bg-white">
                          <button
                            type="button"
                            className="w-full text-left p-3 flex items-start gap-3 hover:bg-slate-50"
                            data-testid={`audit-row-${log.id}`}
                            onClick={() => setExpandedAudit(open ? null : log.id)}
                          >
                            <div className="p-2 bg-slate-50 rounded-lg border">
                              <Activity className="h-4 w-4 text-slate-500" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center justify-between gap-2">
                                <p className="font-medium text-slate-900">{s.action}</p>
                                <span className="text-xs text-slate-500 whitespace-nowrap">
                                  {s.timestamp ? new Date(s.timestamp).toLocaleString() : ''}
                                </span>
                              </div>
                              <div className="flex items-center gap-2 mt-1 flex-wrap">
                                {s.target && <span className="text-sm text-slate-500">{s.target}</span>}
                                {s.result && (
                                  <Badge variant="outline" className="text-xs capitalize">{s.result}</Badge>
                                )}
                                {imp && (
                                  <Badge className="text-xs bg-amber-100 text-amber-900 border-amber-200">
                                    Acting as another user
                                  </Badge>
                                )}
                              </div>
                              {s.actor && <p className="text-xs text-slate-400 mt-1">By: {s.actor}</p>}
                            </div>
                            <ChevronDown className={`h-4 w-4 text-slate-400 mt-2 transition-transform ${open ? 'rotate-180' : ''}`} />
                          </button>

                          {open && (
                            <div className="border-t px-3 py-3 bg-slate-50" data-testid={`audit-detail-${log.id}`}>
                              <div className="grid grid-cols-2 gap-x-6 gap-y-2">
                                {detailRows(log).map((r) => (
                                  <div key={r.key}>
                                    <p className="text-xs text-slate-500">{r.label}</p>
                                    <p className={`text-sm ${r.mono ? 'font-mono text-xs' : ''} ${r.recorded ? 'text-slate-900' : 'text-slate-400'}`}>
                                      {r.value}
                                    </p>
                                  </div>
                                ))}
                              </div>
                              {imp && (
                                <div className="mt-3 rounded border border-amber-200 bg-amber-50 px-3 py-2">
                                  <p className="text-sm text-amber-900">
                                    Performed while impersonating{' '}
                                    <span className="font-mono">{imp.impersonatedUserId}</span>. Both identities are recorded.
                                  </p>
                                </div>
                              )}
                              {hasChange(log) && (
                                <div className="mt-3 grid grid-cols-2 gap-3">
                                  <div>
                                    <p className="text-xs text-slate-400 mb-1">Before</p>
                                    <pre className="p-2 bg-red-50 border border-red-100 rounded text-xs overflow-auto max-h-40">
                                      {log.old_value ? JSON.stringify(log.old_value, null, 2) : '(none)'}
                                    </pre>
                                  </div>
                                  <div>
                                    <p className="text-xs text-slate-400 mb-1">After</p>
                                    <pre className="p-2 bg-green-50 border border-green-100 rounded text-xs overflow-auto max-h-40">
                                      {log.new_value ? JSON.stringify(log.new_value, null, 2) : '(none)'}
                                    </pre>
                                  </div>
                                </div>
                              )}
                              {log.details && Object.keys(log.details).length > 0 && (
                                <details className="mt-3 rounded border border-slate-200 bg-white">
                                  <summary className="cursor-pointer px-3 py-2 text-sm font-medium text-slate-600">
                                    Technical detail
                                  </summary>
                                  <pre className="p-3 bg-slate-50 text-xs overflow-auto max-h-48">
                                    {JSON.stringify(log.details, null, 2)}
                                  </pre>
                                  <p className="px-3 pb-2 text-xs text-slate-400">
                                    Secrets are replaced with [redacted] before an audit row is written.
                                  </p>
                                </details>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}

                    <div className="flex items-center justify-between pt-2">
                      <p className="text-xs text-slate-500">
                        {auditSkip + 1}–{Math.min(auditSkip + auditLogs.length, auditTotal)} of {auditTotal}
                      </p>
                      <div className="flex gap-2">
                        <Button variant="outline" size="sm" disabled={auditSkip === 0}
                                onClick={() => fetchAuditLogs(Math.max(0, auditSkip - AUDIT_PAGE_SIZE), auditFilters)}>
                          Previous
                        </Button>
                        <Button variant="outline" size="sm"
                                disabled={auditSkip + auditLogs.length >= auditTotal}
                                onClick={() => fetchAuditLogs(auditSkip + AUDIT_PAGE_SIZE, auditFilters)}>
                          Next
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })()}
            </CardContent>
          </Card>
        </TabsContent>

        {/* SUPPORT TAB */}
      </Tabs>

      {/* Reset Password Dialog */}
      <Dialog open={!!resetPasswordUser} onOpenChange={(open) => !open && setResetPasswordUser(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reset Password</DialogTitle>
            <DialogDescription>
              Set a new password for {resetPasswordUser?.first_name} {resetPasswordUser?.last_name} ({resetPasswordUser?.email}).
              They will need to use this password on their next login.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="new-password">New password</Label>
            <Input
              id="new-password"
              type="password"
              placeholder={`At least ${passwordPolicy.min_length} characters`}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              autoComplete="new-password"
            />
            {newPassword.length > 0 && passwordPolicyErrors(newPassword, passwordPolicy).map((message) => (
              <p key={message} className="text-xs text-red-600">{message}</p>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResetPasswordUser(null)} disabled={resettingPassword}>
              Cancel
            </Button>
            <Button
              onClick={handleResetPassword}
              disabled={passwordPolicyErrors(newPassword, passwordPolicy).length > 0 || resettingPassword}
            >
              {resettingPassword ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Resetting...
                </>
              ) : (
                'Reset Password'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Login as user — the operator's reason, then startViewAs. */}
      <ImpersonationReasonDialog
        target={viewAsTarget}
        post={(path, body) => axios.post(`${CONTROL_PLANE_API}${path}`, body, { headers })}
        onClose={() => setViewAsTarget(null)}
      />

      {/* Assign Role Dialog */}
      <Dialog open={!!roleDialogUser} onOpenChange={(open) => !open && setRoleDialogUser(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Hierarchy Role</DialogTitle>
            <DialogDescription>
              Where {roleDialogUser?.first_name} {roleDialogUser?.last_name} sits in this
              tenant's role hierarchy. This decides which records they can see through
              hierarchy — not what they are allowed to do.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {/* Effective access is a different question with a different
                answer, so it is shown as its own fact rather than implied by
                whatever is selected below. */}
            <div className="rounded border border-slate-200 bg-slate-50 px-3 py-2 text-xs">
              <div className="font-medium text-slate-700">Effective access</div>
              <div className="mt-1 text-slate-600">
                {roleDialogUser?.is_system_administrator ? (
                  <>
                    <strong>System Administrator</strong>, granted by permission set.
                    Changing the hierarchy role below will not alter it.
                  </>
                ) : (
                  <>Granted by permission sets, which this portal does not change.</>
                )}
              </div>
            </div>

            <Label>Hierarchy role</Label>
            {rolesLoadError ? (
              <p className="text-xs text-red-600">{rolesLoadError}</p>
            ) : (
              <>
                <Select value={roleDialogValue} onValueChange={setRoleDialogValue}>
                  <SelectTrigger data-testid="hierarchy-role-select">
                    <SelectValue placeholder="Select a role" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NO_HIERARCHY_ROLE}>No hierarchy role assigned</SelectItem>
                    {hierarchyRoles.map((r) => (
                      <SelectItem key={r.id} value={r.id}>
                        {'\u00A0'.repeat(r.depth * 2)}{r.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {hierarchyRoles.length === 0 && (
                  <p className="text-xs text-slate-500">
                    This tenant has no roles in its hierarchy yet.
                  </p>
                )}
              </>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRoleDialogUser(null)} disabled={savingRole}>
              Cancel
            </Button>
            <Button onClick={handleAssignRole} disabled={savingRole}>
              {savingRole ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                'Save Role'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create User Dialog */}
      <Dialog open={createUserOpen} onOpenChange={setCreateUserOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add User</DialogTitle>
            <DialogDescription>
              Create a new user within {tenant?.tenant_name || 'this tenant'}.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="new-user-first-name">First name</Label>
                <Input
                  id="new-user-first-name"
                  value={createUserForm.first_name}
                  onChange={(e) => setCreateUserForm({ ...createUserForm, first_name: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="new-user-last-name">Last name</Label>
                <Input
                  id="new-user-last-name"
                  value={createUserForm.last_name}
                  onChange={(e) => setCreateUserForm({ ...createUserForm, last_name: e.target.value })}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="new-user-email">Email</Label>
              <Input
                id="new-user-email"
                type="email"
                value={createUserForm.email}
                onChange={(e) => setCreateUserForm({ ...createUserForm, email: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="new-user-password">Temporary password</Label>
              <Input
                id="new-user-password"
                type="password"
                placeholder={`At least ${passwordPolicy.min_length} characters`}
                value={createUserForm.password}
                onChange={(e) => setCreateUserForm({ ...createUserForm, password: e.target.value })}
                autoComplete="new-password"
              />
              {createUserForm.password.length > 0 && passwordPolicyErrors(createUserForm.password, passwordPolicy).map((message) => (
                <p key={message} className="text-xs text-red-600">{message}</p>
              ))}
            </div>
            <div className="space-y-2">
              <Label>Hierarchy role</Label>
              <Select
                value={createUserForm.role_id || NO_HIERARCHY_ROLE}
                onValueChange={(v) => setCreateUserForm({
                  ...createUserForm,
                  role_id: v === NO_HIERARCHY_ROLE ? null : v,
                })}
              >
                <SelectTrigger data-testid="create-user-hierarchy-role">
                  <SelectValue placeholder="Select a role" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NO_HIERARCHY_ROLE}>No hierarchy role assigned</SelectItem>
                  {hierarchyRoles.map((r) => (
                    <SelectItem key={r.id} value={r.id}>
                      {'\u00A0'.repeat(r.depth * 2)}{r.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500">
                Decides which records this user can see through hierarchy. What they are
                allowed to do comes from permission sets.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateUserOpen(false)} disabled={creatingUser}>
              Cancel
            </Button>
            <Button
              onClick={handleCreateUser}
              disabled={
                creatingUser ||
                !createUserForm.email ||
                !createUserForm.first_name ||
                !createUserForm.last_name ||
                passwordPolicyErrors(createUserForm.password, passwordPolicy).length > 0
              }
            >
              {creatingUser ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                'Create User'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Tenant Dialog */}
      <Dialog open={editTenantOpen} onOpenChange={setEditTenantOpen}>
        <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Tenant</DialogTitle>
            <DialogDescription>
              Update {tenant.tenant_name || tenant.company_name}'s profile. Changing plan updates its module entitlements.
            </DialogDescription>
          </DialogHeader>
          {editForm && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label htmlFor="edit-tenant-name">Tenant name</Label>
                  <Input
                    id="edit-tenant-name"
                    value={editForm.tenant_name}
                    onChange={(e) => setEditForm({ ...editForm, tenant_name: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-org-name">Organization name</Label>
                  <Input
                    id="edit-org-name"
                    value={editForm.organization_name}
                    onChange={(e) => setEditForm({ ...editForm, organization_name: e.target.value })}
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label htmlFor="edit-industry">Industry</Label>
                  <Input
                    id="edit-industry"
                    value={editForm.industry}
                    onChange={(e) => setEditForm({ ...editForm, industry: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-region">Region</Label>
                  <Input
                    id="edit-region"
                    value={editForm.region}
                    onChange={(e) => setEditForm({ ...editForm, region: e.target.value })}
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label htmlFor="edit-seat-limit">Seat limit</Label>
                  <Input
                    id="edit-seat-limit"
                    type="number"
                    min={1}
                    max={10000}
                    value={editForm.seat_limit}
                    onChange={(e) => setEditForm({ ...editForm, seat_limit: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Plan</Label>
                  <Select
                    value={editForm.plan}
                    onValueChange={(v) => setEditForm({ ...editForm, plan: v })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select plan" />
                    </SelectTrigger>
                    <SelectContent>
                      {editPlans.length > 0 ? (
                        editPlans.map((p) => (
                          <SelectItem key={p.api_name} value={p.api_name}>{p.name}</SelectItem>
                        ))
                      ) : (
                        <SelectItem value={editForm.plan}>{editForm.plan}</SelectItem>
                      )}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {/* Trial. Same control as Create Tenant, same vocabulary: a
                  toggle and a DURATION in days. Turning it off clears the end
                  date; leaving it alone changes nothing. */}
              <div className="rounded-lg border border-border px-3 py-3" data-testid="edit-trial-section">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium">Trial period</p>
                    <p className="text-xs text-muted-foreground">
                      {tenant.is_trial && tenant.trial_ends_at
                        ? `Currently ends ${new Date(tenant.trial_ends_at).toLocaleDateString()}`
                        : 'Not on trial'}
                    </p>
                  </div>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={editForm.is_trial}
                      onChange={(e) => setEditForm({ ...editForm, is_trial: e.target.checked, trial_days: '' })}
                      className="accent-primary"
                      data-testid="edit-trial-toggle"
                    />
                    <span className="text-sm text-muted-foreground">{editForm.is_trial ? 'On' : 'Off'}</span>
                  </label>
                </div>
                {editForm.is_trial && (
                  <div className="mt-3 space-y-1">
                    <Label htmlFor="edit-trial-days">Trial length (days, from today)</Label>
                    <Input
                      id="edit-trial-days"
                      type="number"
                      min={1}
                      max={90}
                      className="w-32"
                      placeholder={tenant.is_trial ? 'Unchanged' : '14'}
                      value={editForm.trial_days}
                      onChange={(e) => setEditForm({ ...editForm, trial_days: e.target.value })}
                      data-testid="edit-trial-days-input"
                    />
                    <p className="text-xs text-muted-foreground">
                      {tenant.is_trial
                        ? 'Leave blank to keep the current end date.'
                        : 'Required to turn a trial on. 1 to 90 days.'}
                    </p>
                  </div>
                )}
              </div>
              {/* EDITIONS — Prospect Finder Basic | Automation. Its own
                  confirmed action, not part of Save: see ProductEditionSection. */}
              <ProductEditionSection tenantId={tenantId} headers={headers}
                                     onChanged={() => fetchTenantData()} />
              {/* ADD-ONS — Engage: Forms and Prospect Capture included,
                  Surveys and Landing Pages optional. Its own confirmed
                  action, not part of Save: see ProductAddonsSection. */}
              <ProductAddonsSection tenantId={tenantId} headers={headers}
                                    canEdit={demoSwitchPermission.allowed}
                                    permissionReason={demoSwitchPermission.reason}
                                    onChanged={() => fetchTenantData()} />
              {(tenant.module_entitlements || []).includes('docflow') && (
                <DocFlowStorageExtraCard tenantId={tenantId} onSaved={fetchStorageQuota} />
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditTenantOpen(false)} disabled={savingTenant}>
              Cancel
            </Button>
            <Button
              onClick={handleSaveTenant}
              disabled={savingTenant || !editForm?.tenant_name?.trim() || !editForm?.organization_name?.trim()}
            >
              {savingTenant ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                'Save Changes'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TenantDetailPage;
